import { getGlobalHeadersAsObject } from "../global-headers";
import { DRFListResponseWrapper } from "../types";

export interface TodoItem {
	id: number;
	text: string;
	done: boolean;
	deleted: boolean;
	position: number;
	person: number | null;
	person_name: string | null;
	assignment: number | null;
	assignment_name: string | null;
	created: string;
	updated: string;
}

export interface CreateTodoItemArgs {
	text: string;
	person?: number | null;
	assignment?: number | null;
}

export type UpdateTodoItemArgs = Partial<
	Pick<TodoItem, "text" | "done" | "deleted" | "position" | "person" | "assignment">
>;

const BASE_URL = "/api/v2/todo-items/";

const jsonHeaders = (): Record<string, string> => ({
	"Content-Type": "application/json",
	...getGlobalHeadersAsObject(),
});

export async function getTodoItems(): Promise<TodoItem[]> {
	const response = await fetch(BASE_URL, {
		method: "GET",
		headers: jsonHeaders(),
	});
	if (!response.ok) {
		throw new Error(`Failed to fetch todo items: ${response.status}`);
	}
	const data: DRFListResponseWrapper<TodoItem[]> = await response.json();
	return data.results;
}

export async function createTodoItem(args: CreateTodoItemArgs): Promise<TodoItem> {
	const response = await fetch(BASE_URL, {
		method: "POST",
		headers: jsonHeaders(),
		body: JSON.stringify(args),
	});
	if (!response.ok) {
		throw new Error(`Failed to create todo item: ${response.status}`);
	}
	return response.json();
}

export async function updateTodoItem(
	id: number,
	args: UpdateTodoItemArgs
): Promise<TodoItem> {
	const response = await fetch(`${BASE_URL}${id}/`, {
		method: "PATCH",
		headers: jsonHeaders(),
		body: JSON.stringify(args),
	});
	if (!response.ok) {
		throw new Error(`Failed to update todo item: ${response.status}`);
	}
	return response.json();
}

/**
 * Remove a task from the user's list. This is a soft delete: it PATCHes
 * ``deleted=true`` rather than issuing a hard DELETE, so the row stays in the
 * DB and can be recovered.
 */
export async function deleteTodoItem(id: number): Promise<void> {
	await updateTodoItem(id, { deleted: true });
}

export async function reorderTodoItems(ids: number[]): Promise<void> {
	const response = await fetch(`${BASE_URL}reorder/`, {
		method: "POST",
		headers: jsonHeaders(),
		body: JSON.stringify({ ids }),
	});
	if (!response.ok) {
		throw new Error(`Failed to reorder todo items: ${response.status}`);
	}
}
